import { ListFilter } from 'lucide-react';
import { type RefObject, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type BulkEditDraft,
  DAYPART_OPTIONS,
  EMPTY_BULK_EDIT_DRAFT,
  ENERGY_OPTIONS,
  type SelectionPresetId,
} from './options.ts';
import { useToolbarPopoverPosition } from './toolbar.tsx';

/** Selection preset menu shown from the task toolbar's selection control. */
export function SelectionPresetPopover({
  triggerRef,
  presets,
  visibleCount,
  onClose,
  onSelect,
}: {
  triggerRef: RefObject<HTMLButtonElement>;
  presets: Array<{ id: SelectionPresetId; label: string }>;
  visibleCount: number;
  onClose(): void;
  onSelect(presetId: SelectionPresetId): void;
}): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  const style = useToolbarPopoverPosition({
    align: 'left',
    menuRef,
    onClose,
    open: true,
    triggerRef,
    width: 224,
  });

  if (typeof document === 'undefined') return <></>;

  return createPortal(
    <div
      ref={menuRef}
      data-testid="task-selection-menu"
      style={style}
      className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
    >
      <p className="eyebrow-label px-2 py-1">{visibleCount} visible</p>
      <div className="space-y-1">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            data-testid={`task-selection-preset-${preset.id}`}
            onClick={() => onSelect(preset.id)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-[color:var(--app-text-muted)] transition hover:bg-white/70 hover:text-[color:var(--app-text)]"
          >
            <ListFilter aria-hidden="true" size={14} className="shrink-0" />
            <span className="truncate">{preset.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** Modal form applying a shared patch to every selected task. */
export function BulkEditDialog({
  selectedCount,
  onApply,
  onClose,
}: {
  selectedCount: number;
  onApply(draft: BulkEditDraft): void;
  onClose(): void;
}): JSX.Element {
  const [draft, setDraft] = useState<BulkEditDraft>(EMPTY_BULK_EDIT_DRAFT);

  const updateDraft = (patch: Partial<BulkEditDraft>): void => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  return (
    <div className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]">
      <form
        data-testid="task-bulk-edit-dialog"
        className="grid max-h-[min(42rem,92vh)] w-[min(40rem,96vw)] gap-4 overflow-auto rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-5 shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          onApply(draft);
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow-label">Bulk Edit</p>
            <h2 className="mt-1 text-lg font-semibold text-[color:var(--app-text)]">
              {selectedCount} selected task{selectedCount === 1 ? '' : 's'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="ui-button ui-button-subtle">
            Close
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Owner
            <input
              value={draft.owner}
              onChange={(event) => updateDraft({ owner: event.target.value })}
              className="ui-input"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            URL
            <input
              value={draft.url}
              onChange={(event) => updateDraft({ url: event.target.value })}
              className="ui-input"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Estimate minutes
            <input
              type="number"
              min="1"
              value={draft.estimateMinutes}
              onChange={(event) => updateDraft({ estimateMinutes: event.target.value })}
              className="ui-input"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Location
            <input
              value={draft.location}
              onChange={(event) => updateDraft({ location: event.target.value })}
              className="ui-input"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Repeat
            <input
              value={draft.recurrence}
              onChange={(event) => updateDraft({ recurrence: event.target.value })}
              placeholder="after 6 weeks, every month, none"
              className="ui-input"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Reminder
            <input
              type="datetime-local"
              value={draft.reminderAt}
              onChange={(event) => updateDraft({ reminderAt: event.target.value })}
              className="ui-input"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Energy
            <select
              value={draft.energy}
              onChange={(event) =>
                updateDraft({ energy: event.target.value as BulkEditDraft['energy'] })
              }
              className="ui-select"
            >
              <option value="">Leave unchanged</option>
              {ENERGY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)]">
            Daypart
            <select
              value={draft.daypart}
              onChange={(event) =>
                updateDraft({ daypart: event.target.value as BulkEditDraft['daypart'] })
              }
              className="ui-select"
            >
              <option value="">Leave unchanged</option>
              {DAYPART_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-medium text-[color:var(--app-text)] sm:col-span-2">
            Notes
            <textarea
              value={draft.notes}
              onChange={(event) => updateDraft({ notes: event.target.value })}
              className="ui-input min-h-24"
            />
          </label>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-[color:var(--app-border)] pt-3">
          <button type="button" onClick={onClose} className="ui-button ui-button-subtle">
            Cancel
          </button>
          <button type="submit" className="ui-button ui-button-primary">
            Apply
          </button>
        </div>
      </form>
    </div>
  );
}
